/* useRevealOnce — attaches `.is-visible` to a section the first time it
   crosses into view, then unobserves so it stays revealed forever (no
   re-animation when scrolling back up). Uses IntersectionObserver — no
   scroll polling. Zero cost after the first reveal. */
import { useEffect, useRef } from 'react';

export function useRevealOnce({ rootMargin = '0px 0px -12% 0px', threshold = 0 } = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // If the user prefers reduced motion, don't wire the observer — show
    // instantly (CSS also enforces this, but this avoids any transient
    // opacity flash on browsers that apply CSS on next frame).
    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      el.classList.add('is-visible');
      return undefined;
    }
    // Already partially on-screen at mount → reveal immediately.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) {
      el.classList.add('is-visible');
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            obs.unobserve(e.target);
          }
        });
      },
      { rootMargin, threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin, threshold]);
  return ref;
}
